import React, { useState, useEffect, Fragment } from "react";
import {
  Segment,
  Header,
  Label,
  Menu,
  Message,
  SegmentProps,
} from "semantic-ui-react";
import { useTranslation } from "react-i18next";
import { Redirect } from "react-router-dom";
import { useDispatch } from "react-redux";
import { createRequestFetchObservationCountsAction } from "../../store/actions/observationListActions";
import {
  useObservationCounts,
  useObservationFilters,
} from "../../store/selectors/observationListSelectors";
import { isLoaded, isLoadErrored } from "../../../../sg-core";
import { SemanticCOLORS } from "semantic-ui-react/dist/commonjs/generic";
import { EObservationStatus } from "../../interfaces/IObservation";
import {
  useLoggedInUser,
  useUsersAppRoles,
} from "../../auth/store/authSelectors";
import { getRouteHrefById } from "../../../../routing";
import { HavainnotRouteId } from "../../../routes";

export type TObservationCounts = {
  new: number;
  onHandling: number;
  ready: number;
};

interface IObservationCountersProps {
  counts?: TObservationCounts;
}

interface ICounterContentProps {
  count: number | string;
  text: string;
  color: SemanticCOLORS;
  status: EObservationStatus;
}

const CounterContent: React.FC<ICounterContentProps | SegmentProps> = ({
  count,
  text,
  color,
  status,
  ...segmentProps
}) => {
  const [clicked, setClicked] = useState(false);
  const { update } = useObservationFilters();

  // Use an effect to postpone the state update when the link is clicked.
  // If we don't postpone it, React will go into an infinite render loop.
  useEffect(() => (clicked ? update({ status }) : undefined), [
    clicked,
    status,
    update,
  ]);

  // Poor mans <RouteLink> -> Redirect after clicked = true
  // We do it like this because we want to run some code on click.
  // Edit: Other way would be to use location state and handle update call in WorklistPage
  if (clicked) {
    return (
      <Redirect to={getRouteHrefById(HavainnotRouteId.WorklistPage, { status })!} />
    );
  }

  return (
    <Segment {...segmentProps}>
      <div className="home-statistic" onClick={() => setClicked(true)}>
        <Header as="h3">{count}</Header>
        <Label color={color}>{text}</Label>
      </div>
    </Segment>
  );
};

const ObservationCounters: React.FC<IObservationCountersProps> = (props) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const whosOptions = [
    {
      value: "all",
      label: t("Kaikkien"),
    },
    {
      value: "own",
      label: t("Omat"),
    },
    {
      value: "responsible",
      label: t("Vastuulla"),
    },
  ];

  const user = useLoggedInUser();
  const roleList = useUsersAppRoles();
  const { filters, update } = useObservationFilters();
  const currentWhose =
    user && filters.creator === user.hprPersonId
      ? "own"
      : user && filters.handler === user.hprPersonId
        ? "responsible"
        : "all";

  useEffect(() => {
    if (isLoaded(roleList)) {
      const countFilters = {
        creator: filters.creator,
        handler: filters.handler,
        roles: roleList.data,
      };
      dispatch(createRequestFetchObservationCountsAction(countFilters));
    }
  }, [dispatch, filters.creator, filters.handler, roleList]);

  const handleItemClick = (e: any, { value }: any) => {
    const creator = value === "own" ? user?.id : undefined;
    const handler = value === "responsible" ? user?.id : undefined;
    update({ creator, handler });
  };

  const observationCounts = useObservationCounts();
  const [visibleCounts, setVisibleCounts] = useState<any>();

  // Update visible counts when new ones are loaded.
  useEffect(() => {
    if (
      isLoaded(observationCounts) &&
      observationCounts.data !== visibleCounts
    ) {
      setVisibleCounts(observationCounts.data);
    }
  }, [observationCounts, visibleCounts]);

  const counts = visibleCounts
    ? visibleCounts
    : { new: "-", onHandling: "-", ready: "-" };

  return (
    <Fragment>
      <Menu secondary className="home-statistics">
        {whosOptions.map((option) => (
          <Menu.Item
            key={option.value}
            active={currentWhose === option.value}
            onClick={handleItemClick}
            value={option.value}
          >
            {option.label}
          </Menu.Item>
        ))}
      </Menu>

      <Segment.Group className="home-statistics">
        <CounterContent
          className="main-statistic"
          textAlign="center"
          color="red"
          count={counts.new}
          status={EObservationStatus.New}
          text={t("Uudet")}
        />

        <Segment.Group horizontal>
          <CounterContent
            textAlign="center"
            color="yellow"
            count={counts.onHandling}
            status={EObservationStatus.InProgress}
            text={t("Käsittelyssä")}
          />
          <CounterContent
            textAlign="center"
            color="green"
            count={counts.ready}
            status={EObservationStatus.Finished}
            text={t("Käsitellyt")}
          />
        </Segment.Group>
      </Segment.Group>

      {isLoadErrored(observationCounts) ? (
        <Message content={t("Havaintojen haku epäonnistui")} negative />
      ) : null}
    </Fragment>
  );
};

const MemoizedObservationCounters = React.memo(ObservationCounters);

export default MemoizedObservationCounters;
